(ns hai.chats.components.message
  "Chat message components"
  (:require
   ["react-markdown$default" :as Markdown]
   ["remark-gfm$default" :as remarkGfm]
   [garden.stylesheet]
   [hai.ui.components :as c]
   [hai.ui.components.spinners :as spinners]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(o/defstyled markdown-content :div
  ;; Prose spacing
  {:line-height "1.6"}

  ;; Headings
  [:h1 :text-2xl :font-bold :mt-6 :mb-4]
  [:h2 :text-xl :font-bold :mt-5 :mb-3]
  [:h3 :text-lg :font-semibold :mt-4 :mb-2]
  [:h4 :text-base :font-semibold :mt-3 :mb-2]
  [:h5 :text-sm :font-semibold :mt-3 :mb-2]
  [:h6 :text-xs :font-semibold :mt-3 :mb-2]

  ;; First heading shouldn't have top margin
  [:*:first-child {:margin-top "0"}]
  [:*:last-child {:margin-bottom "0"}]

  ;; Paragraphs
  [:p :mb-4]

  ;; Lists
  [:ul :list-disc :ml-6 :mb-4]
  [:ol :list-decimal :ml-6 :mb-4]
  [:li :mb-1]
  [:li>ul :mt-2]
  [:li>ol :mt-2]

  ;; Code
  [:pre :bg-gray-100 :rounded :p-3 :mb-4 :overflow-x-auto]
  [:code :bg-gray-100 :rounded :px-1.5 :py-0.5 :text-sm :font-mono :whitespace-pre-wrap]
  [:pre>code :bg-transparent :p-0]

  ;; Links
  [:a :text-blue-600 :underline {:text-decoration-color "rgba(37, 99, 235, 0.3)"}]
  [:a:hover {:text-decoration-color "rgba(37, 99, 235, 1)"}]

  ;; Blockquotes
  [:blockquote :border-l-4 :border-gray-300 :pl-4 :italic :my-4]

  ;; Horizontal rule
  [:hr :border-gray-300 :my-6]

  ;; Strong and emphasis
  [:strong :font-bold]
  [:em :italic]

  ;; Tables
  [:table :w-full :border-collapse :mb-4]
  [:thead :border-b-2 :border-gray-300]
  [:th :text-left :font-semibold :px-3 :py-2]
  [:td :px-3 :py-2 :border-b :border-gray-200]
  [:tr:last-child>td {:border-bottom "none"}]
  [:tbody>tr:hover :bg-gray-50]
  [spinners/streaming-dots :justify-self-end :pr-4]
  ([content & {:keys [streaming?]}]
   [:<>
    [:> Markdown {:remarkPlugins [remarkGfm]}
     content]
    (when streaming?
      [spinners/streaming-dots])]))

(o/defrules msg-fade-in
  (garden.stylesheet/at-keyframes
   :msgFadeIn
   [:from {:opacity 0 :transform "translateY(10px)"}]
   [:to {:opacity 1 :transform "translateY(0px)"}]))

(o/defstyled message-container :div
  :flex
  {:gap c/--space-3
   :padding c/--space-3}

  ;; Animation only plays on initial mount via .animate class
  [:&.animate
   {:animation "msgFadeIn 0.3s ease-out forwards"}]

  [:&.user :flex-row-reverse]
  [:&.assistant :flex-row]
  [:.avatar
   :flex :items-center :justify-center :rounded-full :text-white :font-semibold :flex-shrink-0
   {:width "2.25rem"
    :height "2.25rem"}]
  [:.avatar.user-avatar
   {:background t/--humble-secondary}]
  [:.avatar.assistant-avatar
   {:background t/--humble-primary-dark}]
  [:.bubble :rounded-lg
   {:padding c/--space-3}]
  [:.bubble.user-bubble
   {:background t/--humble-offwhite}]
  [:.bubble.assistant-bubble
   {:background c/--color-surface}]
  [:.timestamp :text-xs :mt-1
   {:color t/--humble-primary}])

(defn message
  "Chat message component that only animates on initial mount.
   Pass :streaming? true to show a blinking cursor at the end."
  [{:keys [role content timestamp streaming?]}]
  (let [!mounted? (r/atom false)]
    (r/create-class
     {:component-did-mount
      (fn [_] (reset! !mounted? true))

      :reagent-render
      (fn [{:keys [role content timestamp streaming?]}]
        (let [is-user? (= role :message.role/user)]
          [message-container
           {:class (cond-> [(name role)]
                     (not @!mounted?) (conj "animate"))}
           [:div.avatar
            {:class (if is-user? "user-avatar" "assistant-avatar")}
            (if is-user? "U" "H")]
           [:div.message-content
            [:div.bubble
             {:class (if is-user? "user-bubble" "assistant-bubble")}
             [markdown-content content {:streaming? streaming?}]]
            #_(when timestamp
                [:div.timestamp timestamp])]]))})))

(o/defstyled message-input :div
  :sticky :bottom-0 :bt-black
  ; <TODO text="remove this quick fix once the layout is fixed">
  :mb-15
  ; </TODO>
  {:background c/--color-surface
   :padding c/--space-3}
  [:.input-container :flex :gap-3]
  [:input :flex-1 :p-3 :border :text-sm
   {:border-color c/--color-surface}]
  ([{:keys [on-send]}]
   [:div.input-container
    [:input {:type "text"
             :placeholder "Type your message..."
             :on-key-down (fn [e]
                            (when (and (= (.-key e) "Enter")
                                       (not (.-shiftKey e)))
                              (.preventDefault e)
                              (when on-send
                                (on-send (.-value (.-target e))))
                              (set! (.-value (.-target e)) "")))}]
    [c/Button {:on-click (fn []
                           (when-let [input (.querySelector js/document "input[type='text']")]
                             (when (not= "" (.-value input))
                               (when on-send
                                 (on-send (.-value input)))
                               (set! (.-value input) ""))))}
     "Send"]]))
